import React from 'react';
import ReactDOM from 'react-dom';
import {useStore} from 'react-redux';

import {TypedUseQueryHookResult} from '@reduxjs/toolkit/query/react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import range from 'lodash/range';

import useLoadByScroll, {TOptions as TLoadByScrollOptions} from '@edna/hooks/useLoadByScroll';

import {ESortDirection} from 'src/constants';
import {axiosBaseQuery} from 'src/models';

import {
  DEFAULT_INDIVIDUAL_FILTERS,
  EListState,
  PAGE_SIZE,
  TFilters,
  TFiltersConfig,
} from './definitions';
import {getDifferenceCount} from './utils';

type TPaginationConfig = {
  pageSize?: number;
};

type TPaginationPayload = {
  limit: number;
  offset: number;
};

const usePagination = ({pageSize = PAGE_SIZE}: TPaginationConfig = {}): {
  data: {pageSize: number; page: number};
  payload: TPaginationPayload;
  payloads: TPaginationPayload[];

  reset: TEmptyFunction;
  increasePage: TAnyFunction;
} => {
  const [page, setPage] = React.useState(0);

  const reset = React.useCallback(() => {
    setPage(0);
  }, []);

  const increasePage = React.useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const payload = React.useMemo(() => {
    return {
      offset: page * pageSize,
      limit: pageSize,
    };
  }, [page, pageSize]);

  const payloads = React.useMemo(() => {
    return range(page + 1).map((pageNumber) => ({offset: pageNumber * pageSize, limit: pageSize}));
  }, [page, pageSize]);

  return {
    data: {pageSize, page},
    payload,
    payloads,
    reset,
    increasePage,
  };
};

type TFiltersPayload = TAnyObject;

const useFilters = <T = TFiltersPayload>({
  action,
  selector,
  defaultFilters,
  individualFilters = DEFAULT_INDIVIDUAL_FILTERS,
  validate = () => true,
}: TFiltersConfig<T> = {}): TFilters<T> => {
  const store = useStore<TRootState>();
  const initialFilters = isEmpty(selector?.(store.getState()))
    ? defaultFilters
    : selector?.(store.getState());
  const [filters, setFilters] = React.useState<T>(initialFilters ?? ({} as T));

  React.useEffect(() => {
    if (action) {
      store.dispatch(action(filters));
    }
  }, [filters, store.dispatch, action]);

  const reset = React.useCallback(() => {
    const defaultFiltersValue = defaultFilters ?? ({} as T);

    setFilters({...defaultFiltersValue, ...pick(filters, individualFilters)});
  }, [defaultFilters, filters, setFilters, individualFilters]);

  return {
    payload: filters,
    data: filters,
    setFilters,
    reset,
    valid: validate(filters),
  };
};

type TSort = {
  property: string;
  direction: ESortDirection;
};

type TSortConfig = {
  defaultPrimarySort?: TSort;
  secondarySort?: TSort;
};

type TSortPayload = {sort?: TSort[]};

const useSort = ({
  defaultPrimarySort = {
    property: 'updatedAt',
    direction: ESortDirection.DESC,
  },
  secondarySort = {
    property: 'id',
    direction: ESortDirection.DESC,
  },
}: TSortConfig = {}): {
  data: {primarySort: TSort};
  payload: TSortPayload;
  reset: TEmptyFunction;
  toggleSort: (property: string, direction?: ESortDirection) => void;
} => {
  const [primarySort, setPrimarySort] = React.useState(defaultPrimarySort);

  const reset = React.useCallback(() => {
    setPrimarySort(defaultPrimarySort);
  }, [defaultPrimarySort]);

  const toggleSort = React.useCallback((property: string, direction?: ESortDirection) => {
    if (direction !== undefined) {
      setPrimarySort({property, direction});

      return;
    }

    setPrimarySort((prevSort) =>
      prevSort.property === property
        ? {
            ...prevSort,
            direction:
              prevSort.direction === ESortDirection.ASC ? ESortDirection.DESC : ESortDirection.ASC,
          }
        : {property, direction: ESortDirection.ASC},
    );
  }, []);

  const payload = React.useMemo(
    () => ({sort: [primarySort, secondarySort]}),
    [primarySort, secondarySort],
  );

  return {data: {primarySort}, payload, reset, toggleSort};
};

type TPayload<T = TFiltersPayload> = TPaginationPayload & TSortPayload & T;

type TData<T> = {content: T[]; first?: boolean; last: boolean; totalElements?: number};

type TUseQuery<T = TAnyObject, F = TFiltersPayload> = (
  data: TPayload<F>,
  options?: {
    skip?: boolean;
  },
) => TypedUseQueryHookResult<TData<T> | undefined, TPayload<F>, ReturnType<typeof axiosBaseQuery>>;

type TListConfig<F = TFiltersPayload, TParams = TAnyObject> = {
  sortConfig?: TSortConfig;
  paginationConfig?: TPaginationConfig;
  filtersConfig?: TFiltersConfig<F>;
  meta?: TParams;
};

export const SKIP_SYMBOL = Symbol('SKIP_SYMBOL');

type TList<T, F> = {
  pages: TPayload<F>[];
  data?: TData<T>;
  loadingState: EListState;
  hasMore: boolean;
};

type TListPayload<F = TFiltersPayload> = {
  filters: F;
  hasFilters: boolean;
  setFilters: React.Dispatch<React.SetStateAction<F>>;
  sort: TSort;
  toggleSort: (property: string, direction?: ESortDirection) => void;
  resetFilters: TEmptyFunction;
  activeFiltersCount: number;
};

const useListCore = <T, F = TFiltersPayload>(
  useQuery: TUseQuery<T, F>,
  {sortConfig, filtersConfig, paginationConfig, meta}: TListConfig<F> = {},
): [
  TList<T, F> & {
    loadMore: TAnyFunction;
    error: ReturnType<TUseQuery<T, F>>['error'];
  },
  TListPayload<F>,
] => {
  const store = useStore<TRootState>();
  const filters = useFilters<F>(filtersConfig);
  const sort = useSort(sortConfig);
  const pagination = usePagination(paginationConfig);
  const hasMoreRef = React.useRef(false);

  const payload = React.useMemo(
    () => ({
      ...filters.payload,
      ...sort.payload,
      ...pagination.payload,
      ...meta,
    }),
    [filters.payload, sort.payload, pagination.payload],
  );

  const {data, isFetching, isUninitialized, error} = useQuery(payload, {skip: !filters.valid});

  const loadingState = React.useMemo(() => {
    if (isFetching) {
      return EListState.PENDING;
    }

    if (error) {
      return EListState.ERROR;
    }

    if (isUninitialized) {
      return EListState.UNINITIALIZED;
    }

    return EListState.SUCCESS;
  }, [error, isFetching]);

  hasMoreRef.current = loadingState === EListState.SUCCESS && Boolean(data) && !data?.last;

  const loadMore = React.useCallback(() => {
    if (hasMoreRef.current) {
      pagination.increasePage();
    }
  }, [pagination.increasePage]);

  const setFilters = React.useCallback(
    (nextFilters: React.SetStateAction<F> | F) => {
      ReactDOM.unstable_batchedUpdates(() => {
        pagination.reset();
        filters.setFilters(nextFilters);
      });
    },
    [filters.setFilters, pagination.reset],
  );

  React.useEffect(() => {
    if (!filtersConfig?.selector) {
      return;
    }

    const unsubscribe = store.subscribe(() => {
      const nextFilters = filtersConfig.selector?.(store.getState()) as F;

      if (nextFilters !== filters.payload) {
        setFilters(nextFilters);
      }
    });

    return () => unsubscribe();
  }, [store, filters.payload]);

  const resetFilters = React.useCallback(() => {
    ReactDOM.unstable_batchedUpdates(() => {
      pagination.reset();
      filters.reset();
    });
  }, [filters.reset, pagination.reset]);

  const toggleSort = React.useCallback(
    (nextSort: string, direction?: ESortDirection) => {
      ReactDOM.unstable_batchedUpdates(() => {
        sort.toggleSort(nextSort, direction);
        pagination.reset();
      });
    },
    [sort.toggleSort, pagination.reset],
  );

  const pages = React.useMemo(
    () =>
      pagination.payloads.map((paginationPayload) => ({
        ...paginationPayload,
        ...sort.payload,
        ...filters.payload,
        ...meta,
        [SKIP_SYMBOL]: !filters.valid,
      })),
    [pagination.payloads, sort.payload, filters.payload, meta],
  );

  return [
    {
      pages,
      data,
      loadingState,
      loadMore,
      error,
      hasMore: hasMoreRef.current,
    },
    {
      filters: filters.data,
      setFilters,
      hasFilters: !isEqual(filters.data, filtersConfig?.defaultFilters),
      activeFiltersCount: getDifferenceCount<F>(filters, filtersConfig),
      sort: sort.data.primarySort,
      toggleSort,
      resetFilters,
    },
  ];
};

const useList = <T, F = TFiltersPayload, P extends TAnyObject = TAnyObject>(
  useQuery: TUseQuery<T, F>,
  config: TListConfig<F, P> = {},
  options: Pick<TLoadByScrollOptions, 'scrollRef' | 'reserve'> = {},
): [TList<T, F>, TListPayload<F>] => {
  const [{data, loadingState, loadMore, pages, hasMore}, payload] = useListCore<T, F>(
    useQuery,
    config,
  );

  useLoadByScroll({
    hasMore,
    loadMore,
    watch: data,
    disableInitialLoad: true,
    ...options,
  });

  return [
    {
      pages,
      data,
      loadingState,
      hasMore,
    },
    payload,
  ];
};

export {useList, useListCore, usePagination, useSort, useFilters};
export type {TData, TPayload, TUseQuery, TList, TListConfig, TListPayload, TFiltersPayload};
